<template>
	<component
		:is="id ? 'a' : 'div'"
		:id="id"
		data-testid="AtomTitle"
		:href="id ? `#${id}` : undefined"
		:class="['flex flex-col', ...containerClasses]"
	>
		<component
			:is="tag"
			class="font-display font-bold text-inherit"
			:class="classes"
		>
			<slot />
		</component>
		<div
			v-if="underline || underlineCenter"
			data-testId="AtomTitle-underline"
			class="rounded bg-current w-28 max-w-[25%] min-w-[4rem]"
			:class="lineClasses"
		/>
	</component>
</template>

<script lang="ts" setup>
import type { SizeType } from '#base/core/types/SizeType';
import type { TitleTagType } from '#base/core/types/TitleTagType';

const props = withDefaults(
	defineProps<{
		id?: string;
		tag: TitleTagType;
		size?: SizeType;
		underline?: boolean;
		underlineCenter?: boolean;
	}>(),
	{
		underline: false,
		underlineCenter: false,
		size: 'md',
	}
);

const containerClasses = computed(() => {
	const classes = {
		'xs': ['gap-2'],
		'sm': ['gap-3'],
		'md': ['gap-4'],
		'lg': ['gap-5'],
		'xl': ['gap-6'],
		'2xl': ['gap-7'],
		'3xl': ['gap-7'],
		'4xl': ['gap-8'],
		'5xl': ['gap-8'],
		'6xl': ['gap-8'],
	};

	return [classes[props.size], props.underlineCenter ? 'items-center' : ''];
});

const classes = computed(() => {
	const classes = {
		'xs': ['text-title-size-xs', 'lg:text-title-size-sm'],
		'sm': ['text-title-size-sm', 'lg:text-title-size-md'],
		'md': ['text-title-size-md', 'lg:text-title-size-lg'],
		'lg': ['text-title-size-lg', 'lg:text-title-size-xl'],
		'xl': ['text-title-size-xl', 'lg:text-title-size-2xl'],
		'2xl': ['text-title-size-2xl', 'lg:text-title-size-3xl'],
		'3xl': ['text-title-size-3xl', 'lg:text-title-size-4xl'],
		'4xl': ['text-title-size-4xl', 'lg:text-display-size-sm'],
		'5xl': ['text-display-size-sm', 'lg:text-display-size-md'],
		'6xl': ['text-display-size-md', 'lg:text-display-size-lg'],
	};

	return classes[props.size];
});

const lineClasses = computed(() => {
	const classes = {
		'xs': ['h-1'],
		'sm': ['h-1'],
		'md': ['h-1.5'],
		'lg': ['h-1.5'],
		'xl': ['h-1.5'],
		'2xl': ['h-1.5'],
		'3xl': ['h-1.5'],
		'4xl': ['h-1.5'],
		'5xl': ['h-1.5'],
		'6xl': ['h-1.5'],
	};

	return classes[props.size];
});
</script>
